import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { EMPTY, forkJoin, of, throwError } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';
import { DiscountService } from '../services/discount.service';
import { PricingPartnerService } from '../services/pricing-partner.service';
import {
  Discount,
  DiscountPriceType,
  DiscountStatus,
  DiscountType,
} from '../types/pricing.types';
import {
  ApproveDiscountsAction,
  CreateDiscountAction,
  DeleteDiscountsAction,
  GetDiscountAction,
  LoadDiscountsListAction,
  LoadPartnerDiscountsListAction,
  SetDiscountAction,
  UpdateDiscountAction,
} from './discount.actions';

const DISCOUNT_STATE_NAME = 'discount';

export interface DiscountStateModel {
  discounts: { [id: string]: Discount };
  pages?: {
    [key: string]: {
      items: string[];
    };
  };
  currentPageSettings: StatePageSettings<DiscountFilters>;
  totalCount?: number;
}

@State<DiscountStateModel>({
  name: DISCOUNT_STATE_NAME,
  defaults: {
    discounts: {},
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class DiscountState {
  private discountService = inject(DiscountService);
  private pricingPartnerService = inject(PricingPartnerService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  @Selector()
  static getDiscountById(state: DiscountStateModel) {
    return (discountId: string) => state.discounts[discountId];
  }

  @Selector()
  static getDiscountsByIds(state: DiscountStateModel) {
    return (discountIds: string[]) =>
      discountIds
        ?.map((discountId) => state.discounts[discountId])
        .filter((discount) => !!discount);
  }

  @Selector()
  static getDiscountsByPartnerId(state: DiscountStateModel) {
    return (partnerId: string) => {
      return Object.values(state.discounts).filter(
        (discount) => discount.partnerId === partnerId
      );
    };
  }

  @Selector()
  static getDiscountList(state: DiscountStateModel) {
    return Object.values(state.discounts);
  }

  @Selector([DiscountState, DiscountState.getCurrentPageIds])
  static getCurrentPage(state: DiscountStateModel, ids: string[]) {
    return ids
      .map((id) => state.discounts[id])
      .filter((discount) => !!discount);
  }

  @Selector()
  static getCurrentPageIds(state: DiscountStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (Object.keys(pages).length === 0 || pageIndex < 0) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: DiscountStateModel): Pagination<DiscountFilters> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Math.ceil(
        totalCount / currentPageSettings.sortSettings.pageSize
      ),
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(LoadDiscountsListAction)
  LoadDiscountsListAction(
    { patchState, getState }: StateContext<DiscountStateModel>,
    { filters, pageToLoad = 1, reload = false }: LoadDiscountsListAction
  ) {
    return this.discountService
      .getDiscountsList({ ...filters, page: pageToLoad })
      .pipe(
        tap((discounts: any) => {
          patchState({
            discounts: {
              ...getState().discounts,
              ...discounts.data.reduce((acc, discount) => {
                acc[discount.id] = discount;
                return acc;
              }, {}),
            },
            totalCount: discounts.totalCount,
            pages: {
              ...getState().pages,
              [pageToLoad - 1]: {
                items: discounts.data.map((discount: Discount) => discount.id),
              },
            },
            currentPageSettings: {
              ...getState().currentPageSettings,
              pageSize: filters.limit,
              pageIndex: pageToLoad - 1,
              sortSettings: {
                ...getState().currentPageSettings.sortSettings,
                pageSize: filters.limit,
              },
            },
          });
        }),
        catchError((error) => {
          if (error.status === HttpStatusCode.Forbidden) {
            this.toastService.danger(
              this.translateService.instant(
                'pricing.errors.missingAccess.message'
              ),
              this.translateService.instant(
                'pricing.errors.missingAccess.title'
              ),
              0
            );
          } else {
            this.toastService.danger(error.error.message, error.error.error, 0);
          }

          patchState({
            discounts: {},
            pages: {},
          });

          return throwError(() => error);
        })
      );
  }

  @Action(SetDiscountAction)
  SetDiscountAction(
    { patchState, getState }: StateContext<DiscountStateModel>,
    { list, prependOnPage }: SetDiscountAction
  ) {
    const pages = { ...getState().pages };

    return patchState({
      ...getState(),
      discounts: {
        ...getState().discounts,
        ...list.reduce<{ [id: string]: Discount }>((acc, discount) => {
          acc[discount.id] = discount;
          return acc;
        }, {}),
      },
      pages: {
        ...pages,
        ...(pages[0]?.items && prependOnPage
          ? {
              [0]: {
                items: [
                  ...list.map((discount) => discount.id),
                  ...pages[0].items,
                ],
              },
            }
          : {}),
      },
    });
  }

  @Action(GetDiscountAction)
  GetDiscountAction(
    { patchState, getState }: StateContext<DiscountStateModel>,
    { id, prependToPage, reload }: GetDiscountAction
  ) {
    if (!reload && getState().discounts[id]) {
      return EMPTY;
    }

    return this.discountService.getDiscount(id).pipe(
      tap((discount: Discount) => {
        patchState({
          discounts: {
            ...getState().discounts,
            [discount.id]: discount,
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return of(null);
      })
    );
  }

  @Action(CreateDiscountAction)
  CreateDiscountAction(
    { dispatch, patchState, getState }: StateContext<DiscountStateModel>,
    { discount }: CreateDiscountAction
  ) {
    return this.discountService.createDiscount(discount).pipe(
      delay(300),
      tap((discounts: Discount[]) => {
        dispatch(new SetDiscountAction(discounts, true));
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  @Action(UpdateDiscountAction)
  UpdateDiscountAction(
    { patchState, getState }: StateContext<DiscountStateModel>,
    { discount }: UpdateDiscountAction
  ) {
    return this.discountService.updateDiscount(discount).pipe(
      tap((updated: Discount) => {
        const existing = getState().discounts[updated.id];

        patchState({
          discounts: {
            ...getState().discounts,
            // The update response carries no partner; keep the loaded one.
            [updated.id]: {
              ...existing,
              ...updated,
              partner: updated.partner?.id ? updated.partner : existing?.partner,
            },
          },
        });
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  @Action(ApproveDiscountsAction)
  ApproveDiscountsAction(
    _context: StateContext<DiscountStateModel>,
    { discountIds }: ApproveDiscountsAction
  ) {
    return forkJoin(
      discountIds.map((discountId) =>
        this.discountService.activateDiscount(discountId)
      )
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return throwError(() => error);
      })
    );
  }

  @Action(DeleteDiscountsAction)
  DeleteDiscountsAction(
    _context: StateContext<DiscountStateModel>,
    { discountIds }: DeleteDiscountsAction
  ) {
    return forkJoin(
      discountIds.map((discountId) =>
        this.discountService.deleteDiscount(discountId)
      )
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return throwError(() => error);
      })
    );
  }

  @Action(LoadPartnerDiscountsListAction)
  LoadPartnerDiscountsListAction(
    { patchState, getState }: StateContext<DiscountStateModel>,
    { partnerId }: LoadPartnerDiscountsListAction
  ) {
    return this.pricingPartnerService
      .getPricingPartnerDiscounts(partnerId)
      .pipe(
        tap((discounts: Discount[]) => {
          patchState({
            discounts: {
              ...getState().discounts,
              ...discounts.reduce((acc, discount) => {
                acc[discount.id] = discount;
                return acc;
              }, {}),
            },
          });
        }),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }
}
export interface DiscountFilters extends ApiFilters {
  ids?: string[];
  page?: number;
  limit?: number;
  partnerId?: string;
  countries?: string[];
  discountType?: DiscountType;
  priceType?: DiscountPriceType;
  validFrom?: Date;
  validTo?: Date;
  notes?: string;
  status?: DiscountStatus;
}
